import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import api from '../../utils/api';
import { useDidStream } from '../../hooks/useDidStream';
import { LiveAvatar } from '../../components/interview/LiveAvatar';
import { toast } from '../../hooks/use-toast';
import { 
  Video, Mic, StopCircle, Play, 
  Volume2, VolumeX, Send, MessageCircle,
  Loader2, CheckCircle, AlertTriangle,
  ChevronRight, Award, BarChart3, RefreshCw,
  Upload, FileText, Sparkles, Camera, Shield
} from 'lucide-react';

const RealtimeInterview = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const interviewerVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  // DS-160 state
  const [ds160File, setDs160File] = useState(null);
  const [ds160Content, setDs160Content] = useState('');
  const [ds160Data, setDs160Data] = useState(null);
  const [analyzingDs160, setAnalyzingDs160] = useState(false);
  const [ds160Analyzed, setDs160Analyzed] = useState(false);
  const [uploadMode, setUploadMode] = useState('pdf'); // 'pdf' or 'text'
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  
  // Interview state
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [feedback, setFeedback] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [useTextMode, setUseTextMode] = useState(false);
  
  // AI state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [interviewerVideoUrl, setInterviewerVideoUrl] = useState(null);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  
  // Video state
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  
  // Completion state
  const [showResults, setShowResults] = useState(false);
  const [finalFeedback, setFinalFeedback] = useState(null);

  // Profile-derived context (used when no DS-160 is uploaded)
  const [profilePreview, setProfilePreview] = useState(null);
  const [visaType, setVisaType] = useState('F1');

  // Real-time D-ID avatar (WebRTC). Falls back to browser TTS if the stream is unavailable.
  const avatar = useDidStream();

  useEffect(() => {
    api.get('/interview/realtime/profile-preview')
      .then(r => { setProfilePreview(r.data); if (r.data.visa_type) setVisaType(r.data.visa_type); })
      .catch(() => {});
  }, []);

  // Make ALEC say something: live avatar first, browser TTS as fallback
  const presentQuestion = async (text) => {
    setGeneratingVideo(true);
    try {
      let ok = false;
      try { ok = await avatar.speak(text); } catch (e) { ok = false; }
      if (!ok) speakText(text);
    } finally {
      setGeneratingVideo(false);
    }
  };

  // Initialize camera on component mount for preview
  useEffect(() => {
    initializeCamera();
    return () => {
      // Cleanup camera on unmount
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Attach the camera stream once per element (re-assigning srcObject on every render causes stutter)
  const attachCamera = (el) => {
    videoRef.current = el;
    if (el && cameraStream && el.srcObject !== cameraStream) {
      el.srcObject = cameraStream;
      el.play().catch(() => {});
    }
  };

  const initializeCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraEnabled(true);
      
      // Setup media recorder for audio
      const audioStream = new MediaStream(stream.getAudioTracks());
      mediaRecorderRef.current = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm'
      });
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        chunksRef.current = [];
      };
      
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        title: 'Camera Access',
        description: 'Camera access denied. You can still use text mode.',
        variant: 'destructive'
      });
    }
  };

  // Handle PDF file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast({
        title: 'Invalid File',
        description: 'Please upload a PDF file',
        variant: 'destructive'
      });
      return;
    }
    
    setDs160File(file);
    setAnalyzingDs160(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        '/interview/realtime/upload-ds160-pdf',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setDs160Data(response.data.parsed_data);
      setDs160Analyzed(true);
      
      toast({
        title: response.data.ai_analysis ? 'DS-160 Analyzed!' : 'PDF Processed',
        description: response.data.ai_analysis 
          ? 'AI has extracted your application details' 
          : 'PDF text extracted. Ready to start.'
      });
      
    } catch (error) {
      setDs160File(null);
      // Global error handler shows toast
    } finally {
      setAnalyzingDs160(false);
    }
  };

  // Analyze text DS-160
  const analyzeDs160Text = async () => {
    if (!ds160Content.trim()) {
      toast({
        title: 'No Content',
        description: 'Please paste your DS-160 information',
        variant: 'destructive'
      });
      return;
    }
    
    setAnalyzingDs160(true);
    try {
      const response = await api.post('/interview/realtime/analyze-ds160', { file_content: ds160Content });
      
      setDs160Data(response.data.parsed_data);
      setDs160Analyzed(true);
      
      toast({
        title: 'DS-160 Analyzed!',
        description: 'Ready to start your interview'
      });
      
    } catch (error) {
      toast({
        title: 'Analysis Error',
        description: error.response?.data?.detail || 'Failed to analyze',
        variant: 'destructive'
      });
    } finally {
      setAnalyzingDs160(false);
    }
  };

  // Start interview session (connects the live avatar in parallel with session creation)
  const startInterview = async () => {
    setLoading(true);
    try {
      // Connect the live avatar first (fast), then create the session
      await avatar.connect();
      const response = await api.post('/interview/realtime/start', { ds160_data: ds160Data, visa_type: visaType });
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.message);
      setQuestionNumber(1);
      setSessionStarted(true);
      
      // Add to transcript
      setTranscript([{
        role: 'ai',
        message: response.data.message,
        timestamp: new Date().toISOString()
      }]);
      
      await presentQuestion(response.data.message);
      
      toast({
        title: 'Interview Started',
        description: 'Your visa interview practice has begun'
      });
      
    } catch (error) {
      // Global error handler shows toast
    } finally {
      setLoading(false);
    }
  };

  // Browser TTS fallback
  const speakText = (text) => {
    if (isMuted || !('speechSynthesis' in window)) return;
    
    setIsSpeaking(true);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 0.8;
    
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  };

  // Handle interviewer video end
  const handleVideoEnded = () => {
    setIsSpeaking(false);
  };

  // Start recording
  const startRecording = () => {
    if (!mediaRecorderRef.current) return;
    
    chunksRef.current = [];
    mediaRecorderRef.current.start();
    setIsRecording(true);
    setRecordingTime(0);
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Recording timer
  useEffect(() => {
    let timer;
    if (isRecording) {
      timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRecording]);

  // Send recorded audio
  const sendResponse = async () => {
    if (!audioBlob) {
      toast({
        title: 'No Recording',
        description: 'Please record your response first',
        variant: 'destructive'
      });
      return;
    }
    
    setIsThinking(true);
    avatar.acknowledge();
    
    try {
      // Transcribe audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'response.webm');
      
      const transcribeResponse = await api.post(
        `/interview/realtime/${sessionId}/transcribe`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const userText = transcribeResponse.data.text;
      
      // Add to transcript
      setTranscript(prev => [...prev, {
        role: 'user',
        message: userText,
        timestamp: new Date().toISOString()
      }]);
      
      // Get AI response
      const aiResponse = await api.post(`/interview/realtime/${sessionId}/respond`, { text: userText });
      
      setCurrentQuestion(aiResponse.data.message);
      setQuestionNumber(aiResponse.data.question_number);
      setFeedback(aiResponse.data.feedback);
      
      setTranscript(prev => [...prev, {
        role: 'ai',
        message: aiResponse.data.message,
        timestamp: new Date().toISOString()
      }]);
      
      await presentQuestion(aiResponse.data.message);
      
      setAudioBlob(null);
      
    } catch (error) {
      // Global error handler shows toast
    } finally {
      setIsThinking(false);
    }
  };

  // Send text response
  const sendTextResponse = async () => {
    if (!textInput.trim()) return;
    
    setIsThinking(true);
    avatar.acknowledge();
    
    try {
      setTranscript(prev => [...prev, {
        role: 'user',
        message: textInput,
        timestamp: new Date().toISOString()
      }]);
      
      const aiResponse = await api.post(`/interview/realtime/${sessionId}/respond`, { text: textInput });
      
      setCurrentQuestion(aiResponse.data.message);
      setQuestionNumber(aiResponse.data.question_number);
      setFeedback(aiResponse.data.feedback);
      
      setTranscript(prev => [...prev, {
        role: 'ai',
        message: aiResponse.data.message,
        timestamp: new Date().toISOString()
      }]);
      
      await presentQuestion(aiResponse.data.message);
      
      setTextInput('');
      
    } catch (error) {
      // Global error handler shows toast
    } finally {
      setIsThinking(false);
    }
  };

  // Complete interview
  const completeInterview = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/interview/realtime/${sessionId}/complete`, {});
      
      setFinalFeedback(response.data.feedback);
      setShowResults(true);
      
      // Stop camera and the live avatar
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
      avatar.disconnect();
      
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to complete interview',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Pre-interview screen
  if (!sessionStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full mb-4">
              <Video className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Real-Time AI Video Interview</h1>
            <p className="text-slate-400">Practice with ALEC, our realistic AI interviewer, using your DS-160 data</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: DS-160 Upload */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="h-5 w-5 text-emerald-400" />
                  Step 1: Upload DS-160
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Upload your DS-160 PDF for personalized questions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Mode Toggle */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={uploadMode === 'pdf' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUploadMode('pdf')}
                    className={uploadMode === 'pdf' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    PDF Upload
                  </Button>
                  <Button
                    variant={uploadMode === 'text' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setUploadMode('text')}
                    className={uploadMode === 'text' ? 'bg-emerald-600' : 'border-slate-600 text-slate-300'}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Paste Text
                  </Button>
                </div>

                {uploadMode === 'pdf' ? (
                  <div>
                    <Label className="text-slate-300 mb-2 block">
                      Upload DS-160 PDF:
                    </Label>
                    <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="ds160-upload"
                        disabled={ds160Analyzed}
                      />
                      <label htmlFor="ds160-upload" className="cursor-pointer">
                        {ds160File ? (
                          <div className="text-emerald-400">
                            <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                            <p className="font-medium">{ds160File.name}</p>
                          </div>
                        ) : (
                          <div className="text-slate-400">
                            <Upload className="h-8 w-8 mx-auto mb-2" />
                            <p>Click to upload DS-160 PDF</p>
                            <p className="text-sm mt-1">or drag and drop</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-slate-300 mb-2 block">
                      Paste DS-160 information:
                    </Label>
                    <Textarea
                      value={ds160Content}
                      onChange={(e) => setDs160Content(e.target.value)}
                      placeholder="Paste your DS-160 details here..."
                      className="h-48 bg-slate-900 border-slate-600 text-white placeholder-slate-500"
                      disabled={ds160Analyzed}
                    />
                    {!ds160Analyzed && (
                      <Button
                        onClick={analyzeDs160Text}
                        disabled={analyzingDs160 || !ds160Content.trim()}
                        className="w-full mt-3 bg-blue-600 hover:bg-blue-700"
                      >
                        {analyzingDs160 ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</>
                        ) : (
                          <><Sparkles className="h-4 w-4 mr-2" /> Analyze</>
                        )}
                      </Button>
                    )}
                  </div>
                )}

                {/* Analysis Result */}
                {ds160Analyzed && ds160Data && (
                  <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-emerald-400 mb-3">
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">DS-160 Analyzed!</span>
                    </div>
                    <div className="text-slate-300 text-sm space-y-1">
                      <p><span className="text-slate-500">Name:</span> {ds160Data.personal_info?.full_name || 'N/A'}</p>
                      <p><span className="text-slate-500">Purpose:</span> {ds160Data.travel_purpose || 'N/A'}</p>
                      <p><span className="text-slate-500">Nationality:</span> {ds160Data.personal_info?.nationality || 'N/A'}</p>
                      {ds160Data.us_destination?.institution && (
                        <p><span className="text-slate-500">Destination:</span> {ds160Data.us_destination.institution}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDs160Analyzed(false);
                        setDs160Data(null);
                        setDs160File(null);
                        setDs160Content('');
                      }}
                      className="mt-3 border-slate-600 text-slate-300"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Re-upload
                    </Button>
                  </div>
                )}

                {analyzingDs160 && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-500 mr-2" />
                    <span className="text-slate-400">Analyzing your DS-160...</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right: Camera Preview & Start */}
            <div className="space-y-6">
              {/* Visa type + profile context */}
              <Card className="bg-slate-800/50 border-slate-700" data-testid="visa-profile-card">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-400" />
                    Step 2: Your Visa Type
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    ALEC tailors the interview to your visa and school
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {(profilePreview?.visa_types || [{ code: 'F1', label: 'F-1 Student Visa' }]).map(v => (
                      <button
                        key={v.code}
                        data-testid={`visa-type-${v.code.replace('/', '-').toLowerCase()}`}
                        onClick={() => setVisaType(v.code)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          visaType === v.code
                            ? 'bg-emerald-500 text-white border-emerald-400'
                            : 'bg-slate-700/40 text-slate-300 border-slate-600 hover:bg-slate-700'
                        }`}
                      >
                        {v.label.replace(' Visa', '')}
                      </button>
                    ))}
                  </div>
                  {!ds160Analyzed && profilePreview && (
                    <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 text-sm" data-testid="profile-preview">
                      <p className="text-slate-400 mb-2">No DS-160? ALEC will use your Wayo profile:</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-300">
                        <p><span className="text-slate-500">Name:</span> {profilePreview.full_name || '—'}</p>
                        <p><span className="text-slate-500">Nationality:</span> {profilePreview.nationality || 'Not on profile'}</p>
                        <p className="col-span-2"><span className="text-slate-500">School:</span> {profilePreview.institution || 'Not set — ALEC will ask you'}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Camera Preview */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Camera className="h-5 w-5 text-blue-400" />
                    Camera Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
                    <video
                      ref={attachCamera}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {!cameraEnabled && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
                        <Video className="h-12 w-12 mb-2" />
                        <p>Camera not available</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={initializeCamera}
                          className="mt-2 border-slate-600"
                        >
                          Enable Camera
                        </Button>
                      </div>
                    )}
                    {cameraEnabled && (
                      <Badge className="absolute bottom-2 left-2 bg-green-500 text-white">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Camera Ready
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Tips */}
              <Card className="bg-amber-500/10 border-amber-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-200 font-medium mb-2">Before You Start</p>
                      <ul className="text-amber-200/70 text-sm space-y-1">
                        <li>• Camera & microphone ready</li>
                        <li>• Quiet, well-lit environment</li>
                        <li>• Dress professionally</li>
                        <li>• Upload DS-160 for best experience</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Start Button */}
              <Button
                onClick={startInterview}
                disabled={loading}
                data-testid="start-interview-btn"
                className="w-full h-14 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Preparing Interview...</>
                ) : (
                  <><Play className="h-5 w-5 mr-2" /> Start AI Video Interview</>
                )}
              </Button>
              
              {!ds160Analyzed && (
                <p className="text-center text-slate-500 text-sm">
                  No DS-160? ALEC will personalise questions from your Wayo profile and visa type.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results screen
  if (showResults && finalFeedback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 flex items-center justify-center">
        <Card className="max-w-3xl w-full bg-slate-800/50 border-slate-700">
          <CardHeader className="text-center border-b border-slate-700">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
              <Award className="h-10 w-10 text-white" />
            </div>
            <CardTitle className="text-3xl text-white">Interview Complete!</CardTitle>
            <p className="text-slate-400">Here&apos;s your performance analysis</p>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Score */}
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border-4 border-emerald-500">
                <span className="text-5xl font-bold text-white">{finalFeedback.overall_score}</span>
              </div>
              <p className="text-slate-400 mt-2">Overall Score</p>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{finalFeedback.questions_answered}</p>
                <p className="text-slate-400 text-sm">Questions</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{finalFeedback.average_response_length}</p>
                <p className="text-slate-400 text-sm">Avg Words</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{finalFeedback.content_assessment?.clarity || 'N/A'}</p>
                <p className="text-slate-400 text-sm">Clarity</p>
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <p className="text-slate-300 text-sm">{finalFeedback.summary}</p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300"
                onClick={() => navigate('/interview')}
              >
                Back to Dashboard
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                onClick={() => {
                  setShowResults(false);
                  setSessionStarted(false);
                  setSessionId(null);
                  setTranscript([]);
                  setQuestionNumber(0);
                  setDs160Analyzed(false);
                  setDs160Data(null);
                  initializeCamera();
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Practice Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main interview screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">ALEC - AI Visa Officer</h1>
            <p className="text-slate-400">Question {questionNumber}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-red-500 text-white animate-pulse">
              <StopCircle className="h-3 w-3 mr-1" />
              Live Session
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMuted(!isMuted)}
              className="border-slate-600"
            >
              {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Videos */}
          <div className="lg:col-span-2 space-y-4">
            {/* ALEC - AI Interviewer Video */}
            <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
              <div className="aspect-video bg-slate-900 relative">
                {/* Always show avatar image as background */}
                <img 
                  src={avatar.presenterImage || "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?auto=format&fit=crop&w=600&q=80"}
                  alt="ALEC - AI Interviewer"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Live WebRTC avatar (D-ID Streams). Kept mounted so the track can attach at any time. */}
                <LiveAvatar avatar={avatar} />

                {/* Legacy pre-rendered clip overlay */}
                {interviewerVideoUrl && !avatar.isLive && (
                  <video
                    ref={interviewerVideoRef}
                    src={interviewerVideoUrl}
                    data-testid="alec-video"
                    autoPlay
                    playsInline
                    onEnded={handleVideoEnded}
                    onPlay={() => setIsSpeaking(true)}
                    onError={(e) => console.error('Video error:', e)}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                
                {/* Loading overlay */}
                {(avatar.status === 'connecting' || avatar.status === 'reconnecting' || (generatingVideo && !avatar.isLive)) && !avatar.isSpeaking && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-center">
                      <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-3" />
                      <p className="text-white">{avatar.status === 'reconnecting' ? 'Reconnecting live avatar...' : avatar.status === 'connecting' ? 'Connecting live avatar...' : 'Generating response...'}</p>
                    </div>
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4 bg-blue-900/80 px-3 py-1 rounded-full">
                  <span className="text-white text-xs font-semibold">U.S. CONSULAR OFFICER</span>
                </div>
                {avatar.isLive && (
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-red-600/90 px-2.5 py-1 rounded-full" data-testid="alec-live-badge">
                    <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-xs font-semibold">LIVE</span>
                  </div>
                )}
                {avatar.status === 'failed' && (
                  <div className="absolute top-4 right-4 bg-amber-500/80 px-2.5 py-1 rounded-full" data-testid="alec-voice-badge">
                    <span className="text-white text-xs font-semibold">VOICE MODE</span>
                  </div>
                )}
                
                {(isSpeaking || avatar.isSpeaking) && (
                  <Badge className="absolute bottom-4 right-4 bg-emerald-500 text-white animate-pulse">
                    <Volume2 className="h-3 w-3 mr-1" />
                    Speaking
                  </Badge>
                )}
              </div>
            </Card>
            
            {/* Current Question */}
            <Card className="bg-blue-900/30 border-blue-700/50">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-500 p-2 rounded-full flex-shrink-0">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-blue-300 text-sm font-medium mb-1">Interviewer</p>
                    <p className="text-white text-lg" data-testid="current-question">{currentQuestion}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* User Video & Controls */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* User Camera */}
                  <div className="w-48 h-36 bg-slate-900 rounded-lg overflow-hidden relative flex-shrink-0">
                    <video
                      ref={attachCamera}
                      autoPlay
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    {!cameraEnabled && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Video className="h-8 w-8 text-slate-600" />
                      </div>
                    )}
                    <Badge className="absolute bottom-2 left-2 bg-slate-800/80 text-white text-xs">
                      You
                    </Badge>
                  </div>
                  
                  {/* Response Controls */}
                  <div className="flex-1 flex flex-col justify-center">
                    {/* Mode Toggle */}
                    <div className="flex justify-center gap-2 mb-3">
                      <Button
                        variant={!useTextMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setUseTextMode(false)}
                        className={!useTextMode ? "bg-emerald-600" : "border-slate-600"}
                      >
                        <Mic className="h-4 w-4 mr-1" />
                        Voice
                      </Button>
                      <Button
                        variant={useTextMode ? "default" : "outline"}
                        size="sm"
                        onClick={() => setUseTextMode(true)}
                        className={useTextMode ? "bg-emerald-600" : "border-slate-600"}
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        Type
                      </Button>
                    </div>
                    
                    {useTextMode ? (
                      <div className="space-y-3">
                        <textarea
                          value={textInput}
                          onChange={(e) => setTextInput(e.target.value)}
                          placeholder="Type your response..."
                          className="w-full h-20 bg-slate-900 border border-slate-600 rounded-lg p-3 text-white placeholder-slate-500 resize-none"
                        />
                        <Button
                          onClick={sendTextResponse}
                          disabled={isThinking || !textInput.trim()}
                          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500"
                        >
                          {isThinking ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Send Response
                        </Button>
                      </div>
                    ) : isRecording ? (
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-red-400 font-mono">{formatTime(recordingTime)}</span>
                        </div>
                        <Button
                          onClick={stopRecording}
                          variant="destructive"
                          size="lg"
                          className="w-full"
                        >
                          <StopCircle className="h-5 w-5 mr-2" />
                          Stop Recording
                        </Button>
                      </div>
                    ) : audioBlob ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-center gap-2 text-emerald-400">
                          <CheckCircle className="h-5 w-5" />
                          <span>Recorded ({formatTime(recordingTime)})</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => { setAudioBlob(null); setRecordingTime(0); }}
                            variant="outline"
                            className="flex-1 border-slate-600"
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Re-record
                          </Button>
                          <Button
                            onClick={sendResponse}
                            disabled={isThinking}
                            className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500"
                          >
                            {isThinking ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="h-4 w-4 mr-2" />
                            )}
                            Submit
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p className="text-slate-400 mb-3">Click to start recording</p>
                        <Button
                          onClick={startRecording}
                          disabled={isSpeaking || avatar.isSpeaking || isThinking || generatingVideo}
                          size="lg"
                          className="w-full bg-gradient-to-r from-emerald-500 to-teal-500"
                        >
                          <Mic className="h-5 w-5 mr-2" />
                          Start Speaking
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-4">
            {/* Feedback */}
            {feedback && (
              <Card className="bg-amber-900/30 border-amber-700/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-amber-300 text-sm">Feedback</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-amber-200/70 text-xs">Quality</p>
                    <p className="text-amber-100 text-sm">{feedback.content_quality}</p>
                  </div>
                  <div>
                    <p className="text-amber-200/70 text-xs">Score</p>
                    <div className="flex items-center gap-2">
                      <Progress value={feedback.confidence_score * 10} className="h-2 flex-1" />
                      <span className="text-amber-100 font-bold">{feedback.confidence_score}/10</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Transcript */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Conversation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {transcript.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded text-sm ${
                        msg.role === 'ai' 
                          ? 'bg-blue-900/30 border-l-2 border-blue-500' 
                          : 'bg-emerald-900/30 border-l-2 border-emerald-500'
                      }`}
                    >
                      <p className={`text-xs font-medium mb-1 ${
                        msg.role === 'ai' ? 'text-blue-400' : 'text-emerald-400'
                      }`}>
                        {msg.role === 'ai' ? 'Interviewer' : 'You'}
                      </p>
                      <p className="text-slate-300 line-clamp-3">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* End Interview */}
            <Button
              onClick={completeInterview}
              disabled={loading || questionNumber < 3}
              variant="outline"
              className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <StopCircle className="h-4 w-4 mr-2" />
              )}
              End Interview
            </Button>
            {questionNumber < 3 && (
              <p className="text-slate-500 text-xs text-center">
                Answer 3+ questions to complete
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtimeInterview;
